/* eslint-disable no-param-reassign */
import { COLOR_TRANS } from '../constants/colors';

export const getBlockDarkerColor = (color, darknessFactor = 1) => {
  // If color is transparent, return equivalent of --color-grey-even-darker
  if (color === COLOR_TRANS) {
    return `rgba(83, 80, 102, ${darknessFactor})`;
  }

  // Convert the color to RGB
  const rgb = colorToRgb(color);
  if (!rgb) throw new Error('Invalid color format');

  let [r, g, b] = rgb;
  const a = rgb[3] !== undefined ? rgb[3] : 1;

  // Convert RGB to HSL to preserve saturation
  const hsl = rgbToHsl(r, g, b);

  // Shift the hue (works for all colors)
  // For hues in the range H between 0.08 and 0.17, shift the hue value
  // Positive darknessFactor shifts down, negative shifts up
  if (hsl[0] >= 0.08 && hsl[0] <= 0.17) {
    hsl[0] = Math.max(0, Math.min(1, hsl[0] - 0.05 * darknessFactor));
  }

  // Adjust saturation for vibrancy
  // Positive darknessFactor increases saturation, negative decreases it
  hsl[1] = Math.max(0, Math.min(1, hsl[1] * (1 + 0.1 * darknessFactor)));

  // Adjust luminosity
  // Use different coefficients for darkening vs lightening to avoid washing out colors
  if (darknessFactor >= 0) {
    // Darkening: use stronger effect
    hsl[2] = Math.max(0, Math.min(1, hsl[2] * (1 - 0.5 * darknessFactor)));
  } else {
    // Lightening: adapt coefficient based on original color brightness
    // Dark colors (low luminosity) -> 0.5 coefficient (stronger lightening)
    // Light colors (high luminosity) -> 0.15 coefficient (subtle lightening)
    // Linear interpolation between luminosity 0 and 1
    const lighteningCoefficient = 0.5 - (hsl[2] * 0.35); // 0.5 at L=0, 0.15 at L=1
    hsl[2] = Math.max(0, Math.min(1, hsl[2] * (1 - lighteningCoefficient * darknessFactor)));
  }

  // Convert back to RGB
  [r, g, b] = hslToRgb(hsl[0], hsl[1], hsl[2]);

  // Return in the original format
  return rgbToColorFormat([r, g, b, a], color);
};

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // gray
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return [h, s, l];
};

const hslToRgb = (h, s, l) => {
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

const colorToRgb = (color) => {
  const trimmed = color.trim();

  // Hex format
  if (trimmed.startsWith('#')) {
    return hexToRgb(trimmed);
  }

  // RGB/RGBA format
  // eslint-disable-next-line max-len
  const rgbaMatch = trimmed.match(/rgba?\s*\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+))?\s*\)/i);
  if (rgbaMatch) {
    return [
      parseInt(rgbaMatch[1]),
      parseInt(rgbaMatch[2]),
      parseInt(rgbaMatch[3]),
      parseFloat(rgbaMatch[4] || 1),
    ];
  }

  return null;
};

const hexToRgb = (hex) => {
  let cleanHex = hex.replace('#', '');

  // Short hex (#abc -> #aabbcc)
  if (cleanHex.length === 3) {
    cleanHex = cleanHex.split('').map(char => char + char).join('');
  }

  if (cleanHex.length !== 6) return null;

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return [r, g, b, 1];
};

const rgbToColorFormat = ([r, g, b, a], originalColor) => {
  const trimmed = originalColor.trim();

  // Return as hex
  if (trimmed.startsWith('#')) {
    const hex = [r, g, b]
      .map(x => x.toString(16).padStart(2, '0'))
      .join('');
    return `#${hex}`;
  }

  // Return as rgba/rgb
  if (trimmed.includes('rgba')) {
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }

  return `rgb(${r}, ${g}, ${b})`;
};
